import { useEffect, useState } from "react";
import { useNavigate, Route, Routes } from "react-router-dom";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { errMsg, pub } from "@/lib/api";
import { card, Field, inputCls } from "@/components/admin/kit";
import { staffToken, useStaffApi } from "@/lib/staff";
import { OrgPortal } from "./OrgPortal";
import { Queue } from "@/components/subs/Queue";
import { CaseView } from "@/components/subs/CaseView";

function CheckerApp({ api, user, onLogout }) {
  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6" data-testid="checker-app">
      <div className="mb-4 flex items-center gap-2"><p className="font-bold">OnDose checker · {user.name}</p><Button variant="ghost" size="sm" className="ml-auto" onClick={onLogout} data-testid="checker-logout-btn"><LogOut size={14} /> Log out</Button></div>
      <Routes>
        <Route path="/" element={<Queue api={api} base="/portal" />} />
        <Route path="case/:id" element={<CaseView api={api} base="/portal" />} />
      </Routes>
    </div>
  );
}

function LoginForm({ onDone }) {
  const [f, setF] = useState({ phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = (await pub.post("/auth/login", f)).data; staffToken.set(r.token); onDone(r.user); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid gap-3" data-testid="staff-login-form">
      <p className="text-2xl font-bold">Staff login</p>
      <p className="text-sm text-muted-foreground">For hospital, clinic and pharmacy staff. Your login is created by OnDose.</p>
      <Field label="Phone number"><input className={inputCls} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="staff-phone-input" /></Field>
      <Field label="Password"><input type="password" className={inputCls} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="staff-password-input" /></Field>
      <Button disabled={busy || !f.phone || !f.password} data-testid="staff-login-btn">{busy ? "Checking…" : "Log in"}</Button>
    </form>
  );
}

function ChangePassword({ api, onDone }) {
  const [f, setF] = useState({ old_password: "", new_password: "", again: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.again) { toast.error("The two new passwords don't match"); return; }
    try { await api.post("/auth/change-password", { old_password: f.old_password, new_password: f.new_password }); toast.success("Password changed"); onDone(); } catch (err) { toast.error(errMsg(err)); }
  };
  const inp = (k, l) => <Field label={l}><input type="password" className={inputCls} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`pw-${k}-input`} /></Field>;
  return (
    <form onSubmit={submit} className="grid gap-3" data-testid="change-password-form">
      <p className="text-2xl font-bold">Set your own password</p>
      <p className="text-sm text-muted-foreground">First login: choose a new password (at least 8 characters with one number).</p>
      {inp("old_password", "Temporary password")}{inp("new_password", "New password")}{inp("again", "New password again")}
      <Button data-testid="pw-save-btn">Save password</Button>
    </form>
  );
}

export default function StaffApp() {
  const api = useStaffApi();
  const nav = useNavigate();
  const [user, setUser] = useState(undefined);
  useEffect(() => {
    if (!staffToken.get()) { setUser(null); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { staffToken.clear(); setUser(null); });
  }, [api]);
  const logout = () => { staffToken.clear(); setUser(null); nav("/portal"); };
  if (user === undefined) return null;
  if (user && !user.must_change && user.role === "checker") return <CheckerApp api={api} user={user} onLogout={logout} />;
  if (user && !user.must_change) return <OrgPortal api={api} base="/portal" onLogout={logout} />;
  return (
    <div className="grid min-h-screen place-items-center bg-[var(--lav)] p-4">
      <div className={`${card} w-full max-w-sm`}>
        {!user ? <LoginForm onDone={setUser} /> : <ChangePassword api={api} onDone={() => setUser({ ...user, must_change: false })} />}
      </div>
    </div>
  );
}
